import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Truck, Plus, Search, X, RefreshCw, Loader2,
  Save, Check, ChevronRight, Receipt, ExternalLink,
  Package, UserCheck, Building2, User, ChevronDown, FileX, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import { useSociete } from '../context/SocieteContext';

// ─── Constantes ──────────────────────────────────────────────────────────────

const STATUTS_BL = {
  brouillon: { label: 'Brouillon', bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' },
  livre:     { label: 'Livré',     bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  annule:    { label: 'Annulé',    bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
};

function StatutBadge({ statut }) {
  const s = STATUTS_BL[statut] || STATUTS_BL.brouillon;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function ProgressBar({ lignes = [] }) {
  const totalQte    = lignes.reduce((s, l) => s + (l.quantite || 0), 0);
  const totalLivree = lignes.reduce((s, l) => s + (l.quantite_livree || 0), 0);
  const pct = totalQte > 0 ? Math.min(100, Math.round((totalLivree / totalQte) * 100)) : 0;
  const color = pct >= 100 ? '#22C55E' : pct > 0 ? '#3B82F6' : '#CBD5E1';
  return (
    <div style={{ minWidth: 90 }}>
      <div style={{ height: 5, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden', marginBottom: 2 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, transition: 'width .3s' }} />
      </div>
      <div style={{ fontSize: 10, color: pct >= 100 ? '#15803D' : '#64748B', fontWeight: 600 }}>
        {totalLivree}/{totalQte} · {pct}%
      </div>
    </div>
  );
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

// ─── Panneau de détail BL ─────────────────────────────────────────────────────

function BLDetailPanel({ bl: blInit, onClose, onSaved }) {
  const navigate  = useNavigate();
  const [bl, setBl] = useState(blInit);
  // Matériel sérialisé avec N° de série déjà choisi (verrouillé à la facturation) :
  // la quantité livrée vaut la quantité de la ligne (1 unité) — non modifiable par nature
  const normalizeLignes = (ls) => (ls || []).map(l =>
    l.type_article === 'materiel' && l.materiel_id && !l.quantite_livree
      ? { ...l, quantite_livree: l.quantite || 1 }
      : l
  );
  const [lignes, setLignes]   = useState(() => normalizeLignes(blInit.lignes));
  const [dateLiv, setDateLiv] = useState(blInit.date_livraison || new Date().toISOString().slice(0, 10));
  const [notes, setNotes]     = useState(blInit.notes || '');
  const [saving, setSaving]   = useState(false);
  const [livrant, setLivrant] = useState(false);
  const [factLoading, setFactLoading] = useState(false);
  const [showFactModal, setShowFactModal] = useState(false);
  const [paiLoading, setPaiLoading] = useState(false);
  const [annulLoading, setAnnulLoading] = useState(false);
  const [showAnnulConfirm, setShowAnnulConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('lignes'); // 'lignes' | 'textes'

  // ── Acheteur / Exploitant — hérité du BC (lecture seule sur le BL) ──────────
  // Ces champs sont désormais définis sur le BC et propagés au BL
  // ── Matériels en stock (par article_id) ───────────────────────────────────
  const [materielsStock, setMaterielsStock] = useState({}); // { article_id: [{ materiel_id, numero_serie, annee_modele, ... }] }
  const [loadingStock,   setLoadingStock]   = useState({});
  const [stockDispo,     setStockDispo]     = useState({}); // { article_id: quantite_disponible } pour l'agence du BL

  // Synchronise l'état interne quand le parent met à jour blInit (ex: après onSaved)
  // Critique pour éviter que le bouton "Marquer livré" reste actif avec un statut périmé
  useEffect(() => {
    setBl(blInit);
    if (blInit.statut === 'livre') {
      setLignes(blInit.lignes || []);
    }
  }, [blInit.statut, blInit.bl_id]); // eslint-disable-line

  // Charger les matériels en stock pour les lignes de type matériel
  useEffect(() => {
    const lignesMat = (blInit.lignes || []).filter(l => l.type_article === 'materiel' && l.article_id);
    const articleIds = [...new Set(lignesMat.map(l => l.article_id))];
    articleIds.forEach(async (aid) => {
      if (materielsStock[aid] || loadingStock[aid]) return;
      setLoadingStock(p => ({ ...p, [aid]: true }));
      try {
        // Passer le bl_id courant pour ne pas exclure les matériels déjà dans ce BL
        const r = await api.getMaterielsEnStockParArticle(aid, blInit.agence_id, blInit.bl_id);
        setMaterielsStock(p => ({ ...p, [aid]: r.data || [] }));
      } catch { setMaterielsStock(p => ({ ...p, [aid]: [] })); }
      finally  { setLoadingStock(p => ({ ...p, [aid]: false })); }
    });
  }, []); // eslint-disable-line

  // Charger le stock disponible (articles standard) pour l'agence du BL
  useEffect(() => {
    const artIds = (blInit.lignes || [])
      .filter(l => (l.type_ligne || 'article') === 'article' && l.article_id && l.type_article !== 'materiel')
      .map(l => l.article_id);
    const uniqueIds = [...new Set(artIds)];
    if (!uniqueIds.length || !blInit.agence_id) return;
    api.getStockParAgence(blInit.agence_id, uniqueIds)
      .then(r => {
        const data = r.data || {};
        // Les articles sans entrée en stock ont de facto 0 unités disponibles
        const complete = { ...data };
        for (const id of uniqueIds) {
          if (complete[id] === undefined) complete[id] = 0;
        }
        setStockDispo(complete);
      })
      .catch(() => {});
  }, [blInit.bl_id]); // eslint-disable-line


  const readOnly           = bl.statut === 'annule';
  const qtyReadOnly        = bl.statut === 'livre' || bl.statut === 'annule';
  const attenteFacturation   = bl.statut === 'en_attente_facturation';
  const attentesPaiement     = bl.statut === 'en_attente_paiement';

  // Séparer articles / sections+textes pour les calculs
  const articleLignes = lignes.filter(l => (l.type_ligne || 'article') === 'article');
  const hasTextes     = lignes.some(l => l.type_ligne === 'section' || l.type_ligne === 'texte');

  const lignesSansMatériel   = articleLignes.filter(l => l.type_article === 'materiel' && !l.materiel_id);
  const bloqueSansMatériel   = lignesSansMatériel.length > 0;
  const bloqueLivraison      = attenteFacturation || attentesPaiement || bloqueSansMatériel;
  const canAnnuler         = bl.statut === 'brouillon' || bl.statut === 'en_attente_facturation';

  const totalQte    = articleLignes.reduce((s, l) => s + (l.quantite || 0), 0);
  const totalLivree = articleLignes.reduce((s, l) => s + (l.quantite_livree || 0), 0);
  const pct         = totalQte > 0 ? Math.min(100, Math.round((totalLivree / totalQte) * 100)) : 0;

  const handleSave = async (livrer = false) => {
    if (saving || livrant) return;
    if (livrer) setLivrant(true); else setSaving(true);
    try {
      // Si on livre et qu'aucune quantité n'a été saisie → livraison totale par défaut
      const lignesEff = livrer && totalLivree === 0
        ? lignes.map(l => ({
            ...l,
            quantite_livree: (l.type_ligne || 'article') === 'article'
              ? (l.quantite || 0)
              : (l.quantite_livree || 0),
          }))
        : lignes;

      const lignesUpdated = lignesEff.map(l => ({
        ...l,
        quantite_restante: Math.max(0, (l.quantite || 0) - (l.quantite_livree || 0)),
      }));

      // Construire le payload de sauvegarde
      // On sauvegarde TOUJOURS les lignes avant de livrer (persistance des quantités)
      const savePayload = {
        lignes: lignesUpdated,
        date_livraison: dateLiv || null,
        notes: notes || null,
      };
      // N'inclure le statut que pour les sauvegardes sans livraison sur un BL non-livré
      if (!livrer && bl.statut !== 'livre') {
        savePayload.statut = 'brouillon';
      }
      // Acheteur / Exploitant : hérités du BC et verrouillés — ne pas inclure dans le payload de sauvegarde BL

      // Sauvegarder les lignes (persistance des quantités saisies)
      let res = await api.updateBLVente(bl.bl_id, savePayload);

      if (livrer) {
        // Puis déclencher la livraison (les quantités sont maintenant persistées en base)
        res = await api.livrerBL(bl.bl_id);
      }

      toast.success(livrer ? 'Livraison enregistrée avec succès' : 'Bon de livraison mis à jour');
      onSaved(res.data || res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      if (livrer) setLivrant(false); else setSaving(false);
    }
  };

  const handleFacturer = async () => {
    setFactLoading(true);
    try {
      const res = await api.facturerBLOdoo(bl.bl_id);
      const d = res.data;
      toast.success(`Facture ${d.odoo_invoice_name || ''} créée dans Odoo !`);
      const updated = { ...bl, odoo_invoice_id: d.odoo_invoice_id, odoo_invoice_name: d.odoo_invoice_name };
      if (d.statut) updated.statut = d.statut;
      setBl(updated);
      onSaved(updated);
      setShowFactModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur Odoo');
    } finally {
      setFactLoading(false);
    }
  };

  const handlePaiementRecu = async () => {
    setPaiLoading(true);
    try {
      await api.confirmerPaiementBL(bl.bl_id);
      const updated = { ...bl, statut: 'brouillon' };
      setBl(updated);
      onSaved(updated);
      toast.success('Paiement confirmé — livraison débloquée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setPaiLoading(false);
    }
  };

  const handleAnnuler = async () => {
    setAnnulLoading(true);
    try {
      const res = await api.annulerBL(bl.bl_id);
      const updated = res.data || res;
      setBl(updated);
      onSaved(updated);
      setShowAnnulConfirm(false);
      toast.success('Bon de livraison annulé');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setAnnulLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{bl.numero}</h2>
              <StatutBadge statut={bl.statut} />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text)' }}>{bl.entreprise_nom}</strong>
              {bl.devis_numero && (
                <>
                  {' · BC '}
                  <span
                    onClick={() => navigate(`/ventes?open_devis=${bl.vente_id}`)}
                    style={{ color: 'var(--primary-solid)', cursor: 'pointer', textDecoration: 'underline' }}>
                    {bl.devis_numero}
                  </span>
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Barre de progression globale */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>Avancement</span>
            <span style={{ fontWeight: 700, color: pct >= 100 ? '#15803D' : 'var(--primary-solid)' }}>{pct}%</span>
          </div>
          <div style={{ height: 7, background: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#22C55E' : 'var(--primary-solid)', transition: 'width .4s' }} />
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ padding: '0 24px', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', gap: 0 }}>
        {[
          { key: 'lignes', label: 'Lignes articles' },
          ...(hasTextes ? [{ key: 'textes', label: 'Sections & textes' }] : []),
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px',
              fontSize: 12, fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-secondary)',
              borderBottom: activeTab === tab.key ? '2px solid var(--primary-solid)' : '2px solid transparent',
              marginBottom: -1, whiteSpace: 'nowrap',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>

        {/* ── Onglet Lignes articles ── */}
        {activeTab === 'lignes' && (<>

        {/* Date + Notes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Date de livraison</label>
            <input type="date" value={dateLiv} disabled={readOnly} onChange={e => setDateLiv(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: readOnly ? '#F8FAFC' : '#fff' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Notes</label>
            <input type="text" value={notes} disabled={readOnly} placeholder="Remarques livraison…"
              onChange={e => setNotes(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: readOnly ? '#F8FAFC' : '#fff' }} />
          </div>
        </div>

        {/* Affichage acheteur/exploitant hérité du BC (lecture seule) */}
        {(bl.acheteur_nom || bl.exploitant_bl_nom) && (
          <div style={{ marginBottom: 16, border: '1px solid #E0E7FF', borderRadius: 8, background: '#F5F3FF', padding: '10px 14px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {bl.acheteur_exploitant_locked && (
              <span style={{ fontSize: 10, color: '#2563EB', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 4, padding: '2px 7px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Verrouillé (facture)
              </span>
            )}
            {bl.acheteur_nom && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#7C3AED', fontWeight: 700, marginRight: 4 }}>Acheteur :</span>
                <span style={{ color: '#4C1D95' }}>{bl.acheteur_nom}</span>
              </div>
            )}
            {bl.exploitant_bl_nom && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#7C3AED', fontWeight: 700, marginRight: 4 }}>Exploitant :</span>
                <span style={{ color: '#4C1D95' }}>{bl.exploitant_bl_nom}</span>
              </div>
            )}
          </div>
        )}

        {/* Tableau lignes articles uniquement */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#FAFAFA' }}>
              <tr>
                {['Article', 'Stock dispo', 'Qté cmd', 'Qté livrée', 'Restant', '%'].map((h, i) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: i > 0 ? 'right' : 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articleLignes.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Aucune ligne</td></tr>
              ) : articleLignes.map((lg, idx) => {
                const globalIdx = lignes.indexOf(lg);
                const qte     = lg.quantite || 0;
                const livree  = lg.quantite_livree ?? 0;
                const restant = Math.max(0, qte - livree);
                const pctLg   = qte > 0 ? Math.min(100, Math.round((livree / qte) * 100)) : 0;
                return (
                  <tr key={lg.ligne_id || idx} style={{ borderTop: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '8px 10px' }}>
                      {lg.reference && !lg.reference.startsWith('ART-') && (
                        <span style={{ fontFamily: 'monospace', color: '#94A3B8', marginRight: 6, fontSize: 11 }}>{lg.reference}</span>
                      )}
                      <span style={{ color: 'var(--text)' }}>{lg.designation || '—'}</span>
                      {/* ── Sélecteur de numéro de série (matériel uniquement) ── */}
                      {lg.type_article === 'materiel' && !qtyReadOnly && (
                        <div style={{ marginTop: 6 }}>
                          {lg.numero_serie ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 5, padding: '3px 8px', fontSize: 11 }}>
                              <Package size={11} style={{ color: '#15803D' }} />
                              <span style={{ fontWeight: 600, color: '#15803D' }}>{lg.numero_serie}</span>
                              {lg.annee_modele && <span style={{ color: '#64748B' }}>· {lg.annee_modele}</span>}
                              {/* Verrouillé si sélectionné lors de la facturation Odoo */}
                              {lg.materiel_id_locked ? (
                                <span style={{ marginLeft: 4, fontSize: 10, color: '#2563EB', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 4, padding: '1px 5px' }}>
                                  Verrouillé (facture)
                                </span>
                              ) : (
                                <button onClick={() => {
                                  const next = [...lignes]; next[globalIdx] = { ...lg, materiel_id: null, numero_serie: null, annee_modele: null, compteur_initial_h: null, compteur_initial_km: null, quantite_livree: 0 };
                                  setLignes(next);
                                }} style={{ marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 0, lineHeight: 1 }}><X size={11} /></button>
                              )}
                            </div>
                          ) : (
                            <div>
                              <select
                                data-testid={`select-serie-${lg.ligne_id}`}
                                value=""
                                onChange={e => {
                                  const mid = e.target.value;
                                  const list = materielsStock[lg.article_id] || [];
                                  const mat  = list.find(m => m.materiel_id === mid);
                                  if (!mat) return;
                                  const next = [...lignes];
                                  // Matériel = 1 unité : sélectionner le N° de série fixe automatiquement quantite_livree=1
                                  next[globalIdx] = { ...lg, materiel_id: mat.materiel_id, numero_serie: mat.numero_serie, annee_modele: mat.annee_modele, quantite_livree: 1 };
                                  setLignes(next);
                                }}
                                style={{ fontSize: 11, padding: '3px 6px', border: '1px solid #E2E8F0', borderRadius: 5, maxWidth: 200, color: '#475569' }}>
                                <option value="">— Sélectionner N° de série —</option>
                                {loadingStock[lg.article_id] && <option disabled>Chargement…</option>}
                                {(materielsStock[lg.article_id] || []).map(m => (
                                  <option key={m.materiel_id} value={m.materiel_id}>
                                    {m.numero_serie || '(sans série)'}{m.annee_modele ? ` · ${m.annee_modele}` : ''}
                                  </option>
                                ))}
                                {!loadingStock[lg.article_id] && (materielsStock[lg.article_id] || []).length === 0 && (
                                  <option disabled>Aucun matériel en stock</option>
                                )}
                              </select>
                            </div>
                          )}
                          {/* Compteurs initiaux */}
                          {lg.materiel_id && (() => {
                            const mat = (materielsStock[lg.article_id] || []).find(m => m.materiel_id === lg.materiel_id);
                            if (!mat) return null;
                            return (
                              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                {mat.has_compteur_h && (
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569' }}>
                                    Horamètre initial (h) :
                                    <input
                                      data-testid={`compteur-h-${lg.ligne_id}`}
                                      type="number" min={0} step={0.1}
                                      value={lg.compteur_initial_h ?? ''}
                                      onChange={e => {
                                        const next = [...lignes];
                                        next[globalIdx] = { ...lg, compteur_initial_h: e.target.value === '' ? null : Number(e.target.value) };
                                        setLignes(next);
                                      }}
                                      placeholder="0"
                                      style={{ width: 70, padding: '2px 5px', border: '1px solid #E2E8F0', borderRadius: 4, fontSize: 11, textAlign: 'right' }}
                                    />
                                  </label>
                                )}
                                {mat.has_compteur_km && (
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569' }}>
                                    Km initial :
                                    <input
                                      data-testid={`compteur-km-${lg.ligne_id}`}
                                      type="number" min={0} step={1}
                                      value={lg.compteur_initial_km ?? ''}
                                      onChange={e => {
                                        const next = [...lignes];
                                        next[globalIdx] = { ...lg, compteur_initial_km: e.target.value === '' ? null : Number(e.target.value) };
                                        setLignes(next);
                                      }}
                                      placeholder="0"
                                      style={{ width: 70, padding: '2px 5px', border: '1px solid #E2E8F0', borderRadius: 4, fontSize: 11, textAlign: 'right' }}
                                    />
                                  </label>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {/* Affichage en lecture seule des infos matériel */}
                      {lg.type_article === 'materiel' && qtyReadOnly && lg.numero_serie && (
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#15803D' }}>
                          <Package size={10} />
                          <span style={{ fontWeight: 600 }}>{lg.numero_serie}</span>
                          {lg.annee_modele && <span style={{ color: '#64748B' }}>· {lg.annee_modele}</span>}
                        </div>
                      )}
                    </td>
                    {/* ── Stock disponible agence ── */}
                    {(() => {
                      const dispo = lg.type_article === 'materiel'
                        ? (materielsStock[lg.article_id] || []).length
                        : stockDispo[lg.article_id];
                      const sousStock = dispo !== undefined && dispo < qte;
                      return (
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: sousStock ? '#DC2626' : 'var(--text)', fontWeight: sousStock ? 600 : 400 }}>
                          {dispo !== undefined ? dispo : '—'}
                        </td>
                      );
                    })()}
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)' }}>{qte}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      {qtyReadOnly || lg.type_article === 'materiel' ? (
                        <span style={{ fontWeight: 600, color: pctLg >= 100 ? '#15803D' : 'var(--primary-solid)' }}>{livree}</span>
                      ) : (
                        <input
                          data-testid={`qte-livree-${lg.ligne_id}`}
                          type="number" min={0} max={qte} value={livree}
                          step={lg.nb_decimales === 0 ? 1 : lg.nb_decimales > 0 ? Math.pow(10, -lg.nb_decimales) : 'any'}
                          onChange={e => {
                            const raw = Number(e.target.value);
                            const nbDec = lg.nb_decimales ?? 2;
                            const rounded = nbDec === 0 ? Math.round(raw) : parseFloat(raw.toFixed(nbDec));
                            const next = [...lignes];
                            next[globalIdx] = { ...lg, quantite_livree: Math.min(qte, Math.max(0, rounded)) };
                            setLignes(next);
                          }}
                          style={{ width: 64, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'right', fontSize: 13 }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: restant > 0 ? '#C2410C' : '#15803D' }}>{restant}</td>
                    <td style={{ padding: '8px 10px', minWidth: 80 }}>
                      <div style={{ height: 5, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pctLg}%`, background: pctLg >= 100 ? '#22C55E' : '#3B82F6', transition: 'width .3s' }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#94A3B8', textAlign: 'right', marginTop: 1 }}>{pctLg}%</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Récap */}
        {totalQte > 0 && (
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            {[
              { label: 'Commandé',  value: totalQte,                     bg: '#F8FAFC', border: '#E2E8F0', color: 'var(--text)' },
              { label: 'Livré',     value: totalLivree,                  bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D' },
              { label: 'Restant',   value: Math.max(0, totalQte - totalLivree), bg: '#FFF7ED', border: '#FED7AA', color: '#C2410C' },
            ].map(({ label, value, bg, border, color }) => (
              <div key={label} style={{ flex: 1, background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>
        )}
        </>)}

        {/* ── Onglet Sections & textes ── */}
        {activeTab === 'textes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--text-muted)' }}>
              Ces éléments apparaîtront dans la facture Odoo dans cet ordre.
            </p>
            {lignes.map((lg, idx) => {
              const t = lg.type_ligne;
              if (t !== 'section' && t !== 'texte') return null;
              const isSection = t === 'section';
              return (
                <div key={lg.ligne_id || idx} style={{
                  border: `1px solid ${isSection ? '#C7D2FE' : '#FDE68A'}`,
                  borderRadius: 8, padding: '10px 14px',
                  background: isSection ? '#EEF2FF' : '#FFFBEB',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                      background: isSection ? '#4F46E5' : '#D97706', color: '#fff',
                      textTransform: 'uppercase', letterSpacing: 0.5,
                    }}>
                      {isSection ? 'Section' : 'Texte'}
                    </span>
                    <span style={{ fontSize: 11, color: isSection ? '#4338CA' : '#92400E' }}>
                      Ligne {idx + 1}
                    </span>
                  </div>
                  {readOnly ? (
                    <p style={{ margin: 0, fontSize: 13, color: isSection ? '#1E1B4B' : '#78350F', fontWeight: isSection ? 600 : 400, fontStyle: isSection ? 'normal' : 'italic' }}>
                      {lg.designation || '—'}
                    </p>
                  ) : (
                    <textarea
                      value={lg.designation || ''}
                      onChange={e => {
                        const next = [...lignes];
                        next[idx] = { ...lg, designation: e.target.value };
                        setLignes(next);
                      }}
                      rows={isSection ? 1 : 3}
                      placeholder={isSection ? 'Titre de la section…' : 'Texte du commentaire…'}
                      style={{
                        width: '100%', padding: '6px 10px', border: `1px solid ${isSection ? '#A5B4FC' : '#FCD34D'}`,
                        borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit',
                        fontWeight: isSection ? 600 : 400, background: '#fff', color: isSection ? '#1E1B4B' : '#78350F',
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>{/* end body */}

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '12px 24px', flexShrink: 0 }}>



        {/* ── Annuler le BL ─────────────────────────────────────── */}
        {canAnnuler && (
          showAnnulConfirm ? (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
              <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
                Confirmer l'annulation du BL ?
              </p>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#B91C1C' }}>
                Client : <strong>{bl.entreprise_nom}</strong> · BL : <strong>{bl.numero}</strong>
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowAnnulConfirm(false)}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 7, border: '1px solid #FECACA', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#DC2626' }}>
                  Non
                </button>
                <button data-testid="btn-confirm-annuler-bl" onClick={handleAnnuler} disabled={annulLoading}
                  style={{ flex: 2, padding: '7px 0', borderRadius: 7, background: '#DC2626', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  {annulLoading ? <Loader2 size={12} className="animate-spin" /> : <FileX size={12} />}
                  Annuler le bon de livraison
                </button>
              </div>
            </div>
          ) : (
            <button data-testid="btn-annuler-bl" onClick={() => setShowAnnulConfirm(true)}
              style={{ width: '100%', marginBottom: 10, padding: '8px 0', borderRadius: 8, border: '1px solid #FECACA', background: '#fff', color: '#DC2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              <FileX size={13} />
              Annuler ce bon de livraison
            </button>
          )
        )}

        {bloqueSansMatériel && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 8, background: '#FEF3C7', border: '1px solid #FDE68A', marginBottom: 8 }}>
            <AlertTriangle size={14} style={{ color: '#D97706', marginTop: 1, flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
              <strong>N° de série manquant</strong> — sélectionnez le numéro de série pour :
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {lignesSansMatériel.map(l => (
                  <li key={l.ligne_id}>{l.designation || l.reference || l.article_id}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {!readOnly && bl.statut !== 'livre' && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button data-testid="btn-save-bl" onClick={() => handleSave(false)} disabled={saving || livrant}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', background: '#fff' }}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Enregistrer
            </button>
            {!qtyReadOnly && (
              <button data-testid="btn-livrer-bl" onClick={() => handleSave(true)} disabled={saving || livrant || bloqueLivraison}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-all"
                style={{ background: bloqueLivraison ? '#94A3B8' : '#15803D', cursor: bloqueLivraison ? 'not-allowed' : 'pointer' }}
                title={attenteFacturation ? 'Facturation Odoo requise' : attentesPaiement ? 'En attente de paiement' : ''}>
                {livrant ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                Livrer
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

const COUNTS_INIT = { brouillon: 0, livre: 0, annule: 0 };

export default function BonsLivraisonVentes() {
  const navigate          = useNavigate();
  const [searchParams]    = useSearchParams();
  const { activeAgences } = useSociete();

  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts]   = useState(COUNTS_INIT);
  const [filters, setFilters] = useState({ statut: searchParams.get('statut') || '', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);
  const [panel, setPanel]     = useState(null);
  const [loadingPanelId, setLoadingPanelId] = useState(null);

  const venteIdFromUrl = searchParams.get('vente_id');
  const blIdFromUrl    = searchParams.get('bl_id');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...(venteIdFromUrl ? { vente_id: venteIdFromUrl } : {}), ...filters };
      const [r, ...countRes] = await Promise.all([
        api.getBLVentes(params),
        ...Object.keys(COUNTS_INIT).map(s => api.getBLVentes({ statut: s })),
      ]);
      setData(r.data?.data || []);
      setTotal(r.data?.total || 0);
      const keys = Object.keys(COUNTS_INIT);
      setCounts(Object.fromEntries(keys.map((k, i) => [k, countRes[i].data?.total || 0])));
    } catch { toast.error('Erreur chargement des bons de livraison'); }
    finally { setLoading(false); }
  }, [filters, venteIdFromUrl, activeAgences]);

  useEffect(() => { load(); }, [load]);

  // Ouvrir automatiquement le panel si bl_id est passé en URL (navigation depuis BRV)
  useEffect(() => {
    if (blIdFromUrl) {
      openPanel({ bl_id: blIdFromUrl });
    }
    // eslint-disable-next-line
  }, [blIdFromUrl]);

  const handleSearch = () => setFilters(f => ({ ...f, search: searchInput }));
  const handleStatutFilter = (s) => setFilters(f => ({ ...f, statut: f.statut === s ? '' : s }));

  const handleSaved = (updated) => {
    setData(prev => prev.map(b => b.bl_id === updated.bl_id ? updated : b));
    setPanel({ bl: updated });
    load();
  };

  const openPanel = async (bl) => {
    setLoadingPanelId(bl.bl_id);
    try {
      const r = await api.getBLVente(bl.bl_id);
      setPanel({ bl: r.data });
    } catch {
      toast.error('Erreur lors du chargement du bon de livraison');
    } finally {
      setLoadingPanelId(null);
    }
  };

  return (
    <div className="min-h-screen p-6 space-y-6" style={{ background: 'var(--background)' }}>

      {/* En-tête */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Bons de livraison</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Suivi des livraisons client
            {venteIdFromUrl && <span style={{ marginLeft: 8, color: 'var(--primary-solid)' }}>· filtrés par commande</span>}
          </p>
        </div>
        <button
          data-testid="btn-retour-ventes"
          onClick={() => navigate('/ventes')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
          ← Ventes
        </button>
      </div>

      {/* Stats par statut */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(COUNTS_INIT).map(([key]) => {
          const s = STATUTS_BL[key];
          return (
            <button key={key} onClick={() => handleStatutFilter(key)}
              data-testid={`bl-stat-${key}`}
              className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
              style={{
                background: filters.statut === key ? s.bg : 'var(--surface)',
                borderColor: filters.statut === key ? s.border : 'var(--border)',
              }}>
              <div className="text-2xl font-bold" style={{ color: s.color }}>{counts[key]}</div>
              <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>{s.label}</div>
            </button>
          );
        })}
      </div>

      {/* Barre de recherche */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white flex-1 min-w-48"
          style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={searchInput} onChange={e => { const v = e.target.value; setSearchInput(v); clearTimeout(searchTimer.current); searchTimer.current = setTimeout(() => setFilters(f => ({ ...f, search: v })), 300); }}
            placeholder="N° BL, client, commande…"
            className="flex-1 text-sm focus:outline-none" style={{ color: 'var(--text)', background: 'transparent' }}
            data-testid="bl-search-input" />
          {searchInput && (
            <button onClick={() => { setSearchInput(''); setFilters(f => ({ ...f, search: '' })); }}>
              <X size={12} style={{ color: 'var(--text-muted)' }} />
            </button>
          )}
        </div>
        <button onClick={load} className="p-2 rounded-xl border hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={14} />
        </button>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} BL</span>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <colgroup>
                <col style={{ width: 160 }} />
                <col style={{ width: 250 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 90  }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 65  }} />
                <col style={{ width: 85  }} />
                <col style={{ width: 75  }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                  {['N° BL', 'Client', 'Contact', 'Commande', 'Livraison', 'Avancement', 'Articles', 'Statut', 'Suivi par'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                      style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-16 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                      Aucun bon de livraison — créez-en un depuis une commande validée
                    </td>
                  </tr>
                ) : data.map((bl, i) => (
                  <tr key={bl.bl_id}
                    data-testid={`bl-row-${bl.bl_id}`}
                    onClick={() => openPanel(bl)}
                    className="cursor-pointer transition-colors hover:bg-blue-50/40"
                    style={{ borderBottom: i < data.length - 1 ? '1px solid var(--border-light)' : 'none', opacity: loadingPanelId === bl.bl_id ? 0.6 : 1 }}>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text)' }}>
                      {bl.numero}
                    </td>
                    <td className="px-5 py-3 font-semibold" style={{ color: 'var(--text)' }}>
                      {bl.entreprise_nom || '—'}
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {bl.contact_nom || '—'}
                    </td>
                    <td className="px-5 py-3 text-xs">
                      {bl.devis_numero ? (
                        <span
                          onClick={e => { e.stopPropagation(); navigate(`/ventes?open_devis=${bl.vente_id}`); }}
                          style={{ color: 'var(--primary-solid)', cursor: 'pointer', fontWeight: 600 }}>
                          {bl.devis_numero}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {fmtDate(bl.date_livraison)}
                    </td>
                    <td className="px-5 py-3">
                      <ProgressBar lignes={bl.lignes || []} />
                    </td>
                    <td className="px-5 py-3 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                      {(bl.lignes || []).length}
                    </td>
                    <td className="px-5 py-3">
                      <StatutBadge statut={bl.statut} />
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {(bl.created_by_nom || '').split(' ')[0] || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Side Panel */}
      {panel && (
        <SidePanel
          open={!!panel}
          onOpenChange={(v) => { if (!v) setPanel(null); }}
          title={panel.bl.numero}
          copyValue={panel.bl.numero}
          subtitle={panel.bl.entreprise_nom}
          size="lg"
          headerCenter={
            panel.bl.created_by_nom && (
              <span
                data-testid="bl-affaire-suivie-par"
                style={{ fontSize: '0.72rem', color: 'var(--text-disabled)', fontStyle: 'italic', whiteSpace: 'nowrap' }}
              >
                Affaire suivie par {panel.bl.created_by_nom}
              </span>
            )
          }
        >
          <BLDetailPanel
            bl={panel.bl}
            onClose={() => setPanel(null)}
            onSaved={handleSaved}
          />
        </SidePanel>
      )}
    </div>
  );
}
